import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Box,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import adminService from '../services/adminService';
import {
  formatCurrency,
  formatDateTime,
  getPaymentStatusLabel,
  getPaymentStatusColor,
  getUserTypeLabel,
} from '../utils/helpers';
import { 
  FilterList, 
  AttachMoney, 
  Schedule, 
  CheckCircle, 
  Receipt,
  TrendingUp,
  Payment as PaymentIcon
} from '@mui/icons-material';

const BillingReports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  useEffect(() => {
    loadBillingReports();
  }, [startDate, endDate, statusFilter, userFilter]);

  const loadBillingReports = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (startDate) {
        params.start_date = startDate.toISOString().split('T')[0];
      }
      if (endDate) {
        params.end_date = endDate.toISOString().split('T')[0];
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (userFilter) {
        params.user_id = userFilter;
      }

      const response = await adminService.getBillingReports(params);
      setPayments(response.results || []);
      setSummary(response.summary || null);
    } catch (error) {
      console.error('Error loading billing reports:', error);
      setError('שגיאה בטעינת דוחות תשלומים');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: 'id',
      headerName: 'מזהה',
      width: isMobile ? 80 : 120,
    },
    {
      field: 'user_name',
      headerName: 'שם משתמש',
      width: isMobile ? 120 : 150,
    },
    {
      field: 'user_email',
      headerName: 'מייל',
      width: isMobile ? 150 : 200,
    },
    {
      field: 'user_type',
      headerName: 'סוג משתמש',
      width: isMobile ? 100 : 120,
      renderCell: (params) => (
        <Chip
          label={getUserTypeLabel(params.value)}
          size="small"
          color={params.value === 'trainer' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'amount',
      headerName: 'סכום',
      width: isMobile ? 100 : 120,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 'bold',
            color: 'success.main'
          }}
        >
          {formatCurrency(params.value, params.row.currency)}
        </Typography>
      ),
    },
    {
      field: 'payment_method',
      headerName: 'אמצעי תשלום',
      width: isMobile ? 110 : 130,
      renderCell: (params) => {
        const methodLabels = {
          credit_card: 'כרטיס אשראי',
          paypal: 'PayPal',
          bank_transfer: 'העברה בנקאית',
          bit: 'Bit',
        };
        return (
          <Chip
            label={methodLabels[params.value] || params.value}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.75rem' }}
          />
        );
      },
    },
    {
      field: 'status',
      headerName: 'סטטוס',
      width: isMobile ? 100 : 120,
      renderCell: (params) => (
        <Chip
          label={getPaymentStatusLabel(params.value)}
          size="small"
          color={getPaymentStatusColor(params.value)}
        />
      ),
    },
    {
      field: 'subscription_plan',
      headerName: 'תוכנית',
      width: isMobile ? 100 : 130,
      renderCell: (params) => {
        const planLabels = {
          free_trial: 'תקופת נסיון',
          monthly: 'חודשי',
          yearly: 'שנתי',
        };
        return (
          <Chip
            label={planLabels[params.value] || params.value}
            size="small"
            variant="outlined"
            color="info"
          />
        );
      },
    },
    {
      field: 'transaction_id',
      headerName: 'מזהה עסקה',
      width: isMobile ? 120 : 150,
    },
    {
      field: 'created_at',
      headerName: 'תאריך יצירה',
      width: isMobile ? 130 : 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {formatDateTime(params.value)}
        </Typography>
      ),
    },
    {
      field: 'completed_at',
      headerName: 'תאריך השלמה',
      width: isMobile ? 130 : 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {params.value ? formatDateTime(params.value) : '-'}
        </Typography>
      ),
    },
  ];

  const SummaryCard = ({ title, value, subtitle, icon, color }) => (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: 2,
        boxShadow: theme.shadows[3],
        '&:hover': {
          boxShadow: theme.shadows[6],
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.3s ease-in-out',
        background: `linear-gradient(135deg, ${theme.palette[color].light}15 0%, ${theme.palette[color].main}10 100%)`,
        border: `1px solid ${theme.palette[color].light}`,
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? 40 : 48,
              height: isMobile ? 40 : 48,
              borderRadius: '50%',
              backgroundColor: `${theme.palette[color].main}20`,
              mr: 2,
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                color: `${color}.main`, 
                fontSize: isMobile ? '1.5rem' : '2rem' 
              } 
            })}
          </Box>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            sx={{ 
              fontWeight: 'bold',
              color: `${color}.main`
            }}
          >
            {value}
          </Typography>
        </Box>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ 
            fontSize: isMobile ? '0.8rem' : '0.875rem',
            fontWeight: 500
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ fontSize: '0.75rem' }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: isMobile ? 2 : 4, 
          mb: isMobile ? 2 : 4,
          px: isMobile ? 1 : 3,
        }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            mb: isMobile ? 2 : 3,
            textAlign: isMobile ? 'center' : 'right',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            gap: 1
          }}
        >
          <PaymentIcon sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
          דוחות תשלומים
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
              fontSize: isMobile ? '0.875rem' : '1rem'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        {summary && (
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="סה״כ תשלומים שהושלמו"
                value={formatCurrency(summary.total_completed)}
                icon={<AttachMoney />}
                color="success"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="תשלומים ממתינים"
                value={formatCurrency(summary.total_pending)}
                icon={<Schedule />}
                color="warning"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="תשלומים שהושלמו"
                value={summary.completed_count}
                subtitle="מספר תשלומים"
                icon={<CheckCircle />}
                color="primary"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard
                title="סה״כ תשלומים"
                value={summary.count}
                subtitle="כל התקופה"
                icon={<Receipt />}
                color="info"
              />
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper 
          sx={{ 
            p: isMobile ? 1.5 : 2, 
            mb: 3,
            borderRadius: 2,
            boxShadow: theme.shadows[2],
          }}
        >
          <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="מתאריך"
                value={startDate}
                onChange={setStartDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? 'small' : 'medium',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="עד תאריך"
                value={endDate}
                onChange={setEndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? 'small' : 'medium',
                    sx: {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth variant="outlined" size={isMobile ? 'small' : 'medium'}>
                <InputLabel id="status-select-label">סטטוס תשלום</InputLabel>
                <Select
                  labelId="status-select-label"
                  id="status-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  input={<OutlinedInput label="סטטוס תשלום" />}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                >
                  <MenuItem value="">הכל</MenuItem>
                  <MenuItem value="pending">ממתין</MenuItem>
                  <MenuItem value="completed">הושלם</MenuItem>
                  <MenuItem value="failed">נכשל</MenuItem>
                  <MenuItem value="refunded">זוכה</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                placeholder="מזהה משתמש"
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                label="סינון לפי משתמש"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => {
                  setStartDate(null);
                  setEndDate(null);
                  setStatusFilter('');
                  setUserFilter('');
                }}
                sx={{
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                {isMobile ? 'נקה' : 'נקה מסננים'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Payments Table */}
        <Paper 
          sx={{ 
            height: isMobile ? 500 : 600, 
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[3],
          }}
        >
          <DataGrid
            rows={payments}
            columns={columns}
            pageSize={25}
            rowsPerPageOptions={[25, 50, 100]}
            loading={loading}
            disableSelectionOnClick
            rowHeight={isMobile ? 60 : 70}
            sx={{
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                alignItems: 'center',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #e0e0e0',
                fontWeight: 'bold',
                fontSize: isMobile ? '0.8rem' : '0.875rem',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: '#f9f9f9',
                },
              },
              // Hide columns on smaller screens
              '@media (max-width: 1200px)': {
                '& .MuiDataGrid-columnHeader[data-field="transaction_id"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell[data-field="transaction_id"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader[data-field="completed_at"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell[data-field="completed_at"]': {
                  display: 'none',
                },
              },
              '@media (max-width: 900px)': {
                '& .MuiDataGrid-columnHeader[data-field="subscription_plan"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell[data-field="subscription_plan"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader[data-field="created_at"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell[data-field="created_at"]': {
                  display: 'none',
                },
              },
              '@media (max-width: 700px)': {
                '& .MuiDataGrid-columnHeader[data-field="user_type"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell[data-field="user_type"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader[data-field="payment_method"]': {
                  display: 'none',
                },
                '& .MuiDataGrid-cell[data-field="payment_method"]': {
                  display: 'none',
                },
              },
            }}
          />
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default BillingReports;
